import { Row } from '@tanstack/react-table'
import { useEffect } from 'react'

import { Table, TableColGroup, TableHeader } from '@/ui/Table'
import { Disruption } from '@/types/disruptions'
import { Loader } from '@/ui/Loader'
import { useGetDisruptions } from '../../api/useGetDisruptions'
import { useDisruptionsTable } from '../../hooks/useDisruptionsTable'
import { CreateDisruptionForm } from './forms/CreateDisruptionForm'
import { useDisruptionForm } from '../../hooks/useDisruptionForm'
import { DisruptionRow } from './DisruptionRow'
import { useShowSharedDisruption } from '@/pages/SharedDisruptions/hooks/useShowSharedDisruption'
import { useDisruptionsTableFormStateStore } from '../../store'

import styles from './DisruptionsTable.module.scss'

const NEW_ROW_HIGHLIGHT_DURATION_MS = 10_000

export const DisruptionsTable = () => {
    const { isLoading } = useGetDisruptions()
    const { table, setRowSelection } = useDisruptionsTable()
    const {
        form,
        wrapperRef,
        isCreateFormActive,
        setIsCreateFormActive,
        editWrapperRef,
        handleClickAwayFromForm,
        isSubmitting,
    } = useDisruptionForm({ table })
    const { newlyCreatedReference, clearNewlyCreatedReference } = useDisruptionsTableFormStateStore()

    useShowSharedDisruption(isLoading)

    useEffect(() => {
        if (!newlyCreatedReference) return
        const timer = setTimeout(clearNewlyCreatedReference, NEW_ROW_HIGHLIGHT_DURATION_MS)
        return () => clearTimeout(timer)
    }, [newlyCreatedReference, clearNewlyCreatedReference])

    const handleRowClick = (row: Row<Disruption>) => {
        isCreateFormActive && handleClickAwayFromForm()
        if (!isCreateFormActive) {
            setIsCreateFormActive(false)
            setRowSelection({ [row.id]: true })
        }
    }

    if (isLoading) {
        return <Loader />
    }

    return (
        <div className={styles.root}>
            <div className={styles.formArea}>
                <CreateDisruptionForm
                    form={form}
                    isCreateFormActive={isCreateFormActive}
                    setIsCreateFormActive={setIsCreateFormActive}
                    isSubmitting={isSubmitting}
                    table={table}
                    wrapperRef={wrapperRef}
                />
            </div>

            <div className={styles.tableContainer}>
                <Table className={styles.table}>
                    <TableColGroup table={table} />
                    <TableHeader<Disruption> table={table} />

                    <tbody className={styles.tableBody}>
                        {table.getRowModel().rows.map((row) => (
                            <DisruptionRow
                                key={row.id}
                                row={row}
                                handleRowClick={handleRowClick}
                                table={table}
                                form={form}
                                editWrapperRef={editWrapperRef}
                            />
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    )
}
